import { Stack, Typography } from '@mui/material';
import Divider from '@mui/material/Divider';
import { useNodes } from '@xyflow/react';
import { useDnD } from 'pages/assistants/builder/components/dnDContext';
import { SideBarItem } from 'pages/assistants/builder/components/sidebar/sideBarItem';
import { SideBarExpandableItem } from 'pages/assistants/builder/components/sidebar/sideBarExpandableItem';
import { useGetAssistantToolsQuery } from 'services/assistantApi';

/**
 * Sidebar component displaying draggable flow elements
 */
export function SideBar() {
  const { setNode } = useDnD();
  const nodes = useNodes();

  const hasUserOutput = nodes.some((node) => node.type === 'userOutput');

  // appel de l'API
  const { data: toolsData = [] } = useGetAssistantToolsQuery({ assistantId: 12 });

  const onDragStart = (event, node) => {
    setNode(node);
    event.dataTransfer.effectAllowed = 'move';
  };

  // Regroupement des outils par valeur de group
  const toolsGroupedByCategory = toolsData.reduce((groupedToolsByCategory, currentTool) => {
    const categoryName = currentTool.group || 'Autres';
    if (!groupedToolsByCategory[categoryName]) {
      groupedToolsByCategory[categoryName] = [];
    }
    groupedToolsByCategory[categoryName].push({
      type: `tools`,
      icon: currentTool.icon,
      label: currentTool.name,
      description: currentTool.description,
      color: 'tools',
      toolsId: currentTool.id
    });
    return groupedToolsByCategory;
  }, {});

  // Construction des items de la sidebar
  const sidebarItems = [
    {
      type: 'input',
      icon: 'input_icon',
      label: 'Entrées',
      color: 'infoBlue',
      expandable: true,
      items: [
        { type: 'userInput', icon: 'person_icon', label: 'Utilisateur', color: 'infoBlue' },
        { type: 'mailInput', icon: 'mail_icon', label: 'Email', color: 'infoBlue' }
      ]
    },
    {
      type: 'advisor',
      icon: 'auto_awesome',
      label: 'Processeur',
      color: 'primary',
      expandable: true,
      items: [
        { type: 'rewriteAdvisor', icon: 'draw_icon', label: 'Réécriture', color: 'primary' },
        { type: 'memoryAdvisor', icon: 'sd_card_icon', label: 'Mémoire', color: 'primary' },
        {
          type: 'documentAdvisor',
          icon: 'content_paste_search_icon',
          label: 'Document',
          color: 'primary'
        },
        { type: 'logAdvisor', icon: 'menu_book_icon', label: 'Journalisation', color: 'primary' },
        {
          type: 'groovyProcessor',
          icon: 'terminal_icon',
          label: 'Script Groovy',
          color: 'primary'
        },
        {
          type: 'classificationProcessor',
          icon: 'clear_all_icon',
          label: 'Classification',
          color: 'primary'
        },
        { type: 'switcherProcessor', icon: 'alt_route', label: 'Routage', color: 'primary' },
        { type: 'mailOutput', icon: 'mail_icon', label: 'Envoi Mail', color: 'primary' }
      ]
    },
    { type: 'knowledgeBase', icon: 'school', label: 'Base de connaissance', color: 'dark' },
    {
      type: 'models',
      icon: 'smart_toy_icon',
      label: 'Modèles',
      color: 'error',
      expandable: true,
      items: [
        { type: 'model', icon: 'smart_toy_icon', label: 'Modèle', color: 'error' },
        {
          type: 'longContextModel',
          icon: 'record_voice_over',
          label: 'Modèle transcription',
          color: 'error'
        }
      ]
    },
    { type: 'prompt', icon: 'speaker_notes', label: 'Prompt', color: 'warning' },
    { type: 'account', icon: 'lock_icon', label: 'Compte de connexion', color: 'account' },

    //  additional tools
    ...Object.entries(toolsGroupedByCategory).map(([groupName, groupItems]) => ({
      type: `tools-${groupName}`,
      icon: groupItems[0]?.icon || 'build_icon',
      label: `${groupName}`,
      color: 'tools',
      expandable: true,
      items: groupItems
    })),

    {
      type: 'userOutput',
      icon: 'exit_to_app_rounded_icon',
      label: 'Sortie',
      color: 'success',
      disabled: hasUserOutput
    }
  ];

  return (
    <Stack spacing={2} sx={{ pt: 2, width: 250, borderRight: '1px solid #e0e0e0' }}>
      <Typography variant="h6" textAlign="center">
        Éléments disponibles
      </Typography>
      <Divider />
      <Stack
        spacing={1}
        sx={{ p: 1.5, pt: 0, height: '100%', maxHeight: '83vh', overflowY: 'auto' }}
      >
        {sidebarItems.map((item) =>
          item.expandable ? (
            <SideBarExpandableItem key={item.type} item={item} onDragStart={onDragStart} />
          ) : (
            <SideBarItem key={item.type} item={item} onDragStart={onDragStart} />
          )
        )}
      </Stack>
    </Stack>
  );
}
